import React, { useState, useEffect, useRef } from 'react';
import { Snackbar, Alert, Box, Typography, CircularProgress, Fade } from '@mui/material';

const PWAUpdateManager = () => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
    const [registration, setRegistration] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // ---- Pull-to-refresh indicator state (Safari-like) ----
    const THRESHOLD = 80;     // pull distance that "arms" refresh
    const MAX_OFFSET = 70;    // how far the indicator follows the finger
    const HYSTERESIS = 8;     // ignore tiny jitters
    const ANGLE_LOCK = 0.6;   // vertical intent: dy >= 0.6*|dx|

    const pullingRef = useRef(false);
    const startYRef = useRef(null);
    const startXRef = useRef(null);
    const dyRef = useRef(0);
    const armedRef = useRef(false); // crossed threshold
    const frameRef = useRef(null);

    const [indicatorVisible, setIndicatorVisible] = useState(false);
    const [indicatorOffset, setIndicatorOffset] = useState(0); // px (0..MAX_OFFSET)
    const [indicatorOpacity, setIndicatorOpacity] = useState(0); // 0..1
    const [indicatorSpinning, setIndicatorSpinning] = useState(false); // true when threshold crossed

    const isStandalone = () =>
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        window.navigator.standalone === true;

    const rootScrollTop = () =>
        document.scrollingElement?.scrollTop ??
        document.documentElement.scrollTop ??
        document.body.scrollTop ?? 0;

    const isIgnoredTarget = (target) =>
        !!(target && target.closest('[data-ptf-ignore="true"]'));

    const hasScrollableAncestorAtTop = (el) => {
        let node = el;
        while (node && node !== document.body) {
            const style = window.getComputedStyle(node);
            const overflowY = style.overflowY;
            const canScroll = /(auto|scroll)/.test(overflowY);
            if (canScroll && node.scrollHeight > node.clientHeight) {
                if (node.scrollTop > 0) return true; // let it scroll; don’t arm pull
            }
            node = node.parentElement;
        }
        return false;
    };

    // Render the indicator smoothly without spamming React setState
    const scheduleIndicatorPaint = () => {
        if (frameRef.current) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;

            const dy = Math.max(0, dyRef.current);
            const alpha = Math.min(dy / THRESHOLD, 1);           // fade in to 1 by threshold
            const offset = Math.min(dy, MAX_OFFSET);             // slide down a bit

            setIndicatorVisible(pullingRef.current || dy > 0);
            setIndicatorOpacity(alpha);
            setIndicatorOffset(offset);
            setIndicatorSpinning(armedRef.current);              // spin when armed
        });
    };

    const softRefresh = async () => {
        if (registration?.waiting) {
            setIsUpdating(true);
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            return;
        }
        if (registration?.update) {
            try {
                await registration.update();
            } finally {
                window.location.reload();
            }
        } else {
            window.location.reload();
        }
    };

    // ---- Pull-to-refresh listeners (native scroll preserved) ----
    useEffect(() => {
        // 🔧 TUNABLES
        const THRESHOLD = 80;        // px to ARM refresh
        const MAX_OFFSET = 70;       // px indicator follows finger
        const HYSTERESIS = 8;        // ignore tiny jitters
        const ANGLE_LOCK = 0.6;      // dy must be >= 0.6*|dx|
        const VISIBILITY_OFFSET = 100; // show nothing until this many px pulled
        const LONG_PRESS_MS = 3000;   // treat long-press as selection

        // refs
        const pullingRef = { current: false };
        const startYRef = { current: null };
        const startXRef = { current: null };
        const dyRef = { current: 0 };
        const armedRef = { current: false };
        const frameRef = { current: null };
        const longPressRef = { current: false };
        const selectionActiveRef = { current: false };
        const longPressTimerRef = { current: null };

        const isStandalone = () =>
            (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
            window.navigator.standalone === true;

        const rootScrollTop = () =>
            document.scrollingElement?.scrollTop ??
            document.documentElement.scrollTop ??
            document.body.scrollTop ?? 0;

        const isIgnoredTarget = (target) =>
            !!(target && target.closest('[data-ptf-ignore="true"]'));

        const isSelectableTarget = (target) => {
            if (!target) return false;
            const tag = target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
            if (target.isContentEditable) return true;
            // allow opting a region in:
            if (target.closest('[data-ptf-selectable="true"]')) return true;
            return false;
        };

        const hasScrollableAncestorAtTop = (el) => {
            let node = el;
            while (node && node !== document.body) {
                const style = window.getComputedStyle(node);
                const overflowY = style.overflowY;
                const canScroll = /(auto|scroll)/.test(overflowY);
                if (canScroll && node.scrollHeight > node.clientHeight) {
                    if (node.scrollTop > 0) return true;
                }
                node = node.parentElement;
            }
            return false;
        };

        // smooth paint without spamming React updates
        const schedulePaint = () => {
            if (frameRef.current) return;
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = null;

                // 🔒 NEW: never show indicator during selection/long-press
                if (selectionActiveRef.current || longPressRef.current) {
                    setIndicatorVisible(false);
                    setIndicatorOpacity(0);
                    setIndicatorSpinning(false);
                    setIndicatorOffset(0);
                    return;
                }

                const rawDy = Math.max(0, dyRef.current);
                const visibleDy = Math.max(0, rawDy - VISIBILITY_OFFSET);
                const denom = Math.max(1, THRESHOLD - VISIBILITY_OFFSET);

                const opacity = Math.min(visibleDy / denom, 1);
                const offset = Math.min(rawDy, MAX_OFFSET);
                const spinning = armedRef.current;

                setIndicatorVisible(pullingRef.current && rawDy >= VISIBILITY_OFFSET);
                setIndicatorOpacity(opacity);
                setIndicatorOffset(offset);
                setIndicatorSpinning(spinning);
            });
        };

        const softRefresh = async () => {
            if (registration?.waiting) {
                setIsUpdating(true);
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                return;
            }
            if (registration?.update) {
                try { await registration.update(); } finally { window.location.reload(); }
            } else {
                window.location.reload();
            }
        };

        const resetIndicator = () => {
            setIndicatorVisible(false);
            setIndicatorOpacity(0);
            setIndicatorSpinning(false);
            setIndicatorOffset(0);
        };

        const onTouchStart = (e) => {
            if (!isStandalone()) return;
            if (e.touches.length !== 1) return;

            const target = e.target;
            if (isIgnoredTarget(target)) return;
            if (isSelectableTarget(target)) return;
            if (hasScrollableAncestorAtTop(target)) return;
            if (rootScrollTop() !== 0) return;

            const t = e.touches[0];
            startYRef.current = t.clientY;
            startXRef.current = t.clientX;
            dyRef.current = 0;
            pullingRef.current = false;
            armedRef.current = false;
            longPressRef.current = false;

            if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = setTimeout(() => {
                longPressRef.current = true;       // treat as selection gesture
                // 🔒 NEW: hide immediately if long-press kicks in
                resetIndicator();
            }, LONG_PRESS_MS);

            // start hidden; only becomes visible after VISIBILITY_OFFSET
            resetIndicator();
        };

        const onTouchMove = (e) => {
            if (startYRef.current == null || startXRef.current == null) return;

            // 🔒 NEW: if selection/long-press is active, abort pull entirely
            if (selectionActiveRef.current || longPressRef.current) {
                startYRef.current = null;
                startXRef.current = null;
                dyRef.current = 0;
                pullingRef.current = false;
                armedRef.current = false;
                resetIndicator();
                return;
            }

            const t = e.touches[0];
            const dy = t.clientY - startYRef.current;
            const dx = t.clientX - startXRef.current;

            if (!pullingRef.current) {
                if (Math.abs(dy) < HYSTERESIS && Math.abs(dx) < HYSTERESIS) return;
                if (dy > 0 && Math.abs(dy) >= ANGLE_LOCK * Math.abs(dx)) {
                    pullingRef.current = true;
                } else {
                    startYRef.current = null;
                    startXRef.current = null;
                    dyRef.current = 0;
                    resetIndicator();
                    return;
                }
            }

            dyRef.current = dy;
            armedRef.current = dy >= THRESHOLD;

            // keep native scroll; just paint
            schedulePaint();
        };

        const onTouchEnd = () => {
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }

            const shouldRefresh =
                pullingRef.current &&
                armedRef.current &&
                !longPressRef.current &&
                !selectionActiveRef.current;

            startYRef.current = null;
            startXRef.current = null;
            pullingRef.current = false;
            armedRef.current = false;
            dyRef.current = 0;

            resetIndicator();

            if (shouldRefresh) softRefresh();
        };

        // keep track of real text selection
        let selDebounce;
        const onSelectionChange = () => {
            // debounce slightly to avoid flicker while adjusting selection
            clearTimeout(selDebounce);
            selDebounce = setTimeout(() => {
                try {
                    const sel = window.getSelection && window.getSelection();
                    selectionActiveRef.current =
                        !!sel && sel.type === 'Range' && (sel.toString?.().length || 0) > 0;
                } catch {
                    selectionActiveRef.current = false;
                }
                // If selection is active, ensure indicator is hidden immediately
                if (selectionActiveRef.current) resetIndicator();
            }, 30);
        };

        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: true });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        document.addEventListener('selectionchange', onSelectionChange);

        return () => {
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('selectionchange', onSelectionChange);
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
            clearTimeout(selDebounce);
        };
    }, [registration]);

    // ---- Service worker logic (same as before, with your auto-apply) ----
    useEffect(() => {
        let updateTimer;
        let handleFocus;
        let cleanupListeners;

        if ('serviceWorker' in navigator && !initialized) {
            const initializeServiceWorker = async () => {
                try {
                    const isDevelopment = import.meta.env.MODE === 'development';
                    if (isDevelopment) {
                        setInitialized(true);
                        return;
                    }

                    const existing = await navigator.serviceWorker.getRegistration('/sw.js');
                    const reg =
                        existing ??
                        (await navigator.serviceWorker.register('/sw.js', {
                            updateViaCache: 'imports',
                        }));

                    setRegistration(reg);
                    setInitialized(true);

                    updateTimer = setInterval(() => {
                        if (document.visibilityState === 'visible') reg.update();
                    }, 300000);

                    handleFocus = () => reg.update();
                    window.addEventListener('focus', handleFocus);

                    reg.addEventListener('updatefound', () => {
                        const nw = reg.installing;
                        if (!nw) return;
                        nw.addEventListener('statechange', () => {
                            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
                                setRegistration(reg);
                                setTimeout(() => handleUpdate(), 100);
                            }
                        });
                    });
                } catch (err) {
                    console.error('Service Worker registration failed:', err);
                }
            };

            initializeServiceWorker();
            cleanupListeners = setupUpdateListener();
        }

        return () => {
            if (updateTimer) clearInterval(updateTimer);
            if (handleFocus) window.removeEventListener('focus', handleFocus);
            if (cleanupListeners) cleanupListeners();
        };
    }, [initialized]);

    const setupUpdateListener = () => {
        const messageHandler = (event) => {
            if (event.data && event.data.type === 'SW_UPDATED') {
                setShowUpdateSuccess(true);
                setIsUpdating(false);
            }
        };

        const controllerChangeHandler = () => {
            const isDevelopment = import.meta.env.MODE === 'development';
            if (!isDevelopment && registration && registration.waiting) {
                window.location.reload();
            }
        };

        navigator.serviceWorker.addEventListener('message', messageHandler);
        navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler);

        return () => {
            navigator.serviceWorker.removeEventListener('message', messageHandler);
            navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
        };
    };

    const handleUpdate = async () => {
        if (!registration || !registration.waiting) return;
        setIsUpdating(true);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    };

    const handleDismissSuccess = () => setShowUpdateSuccess(false);

    return (
        <>
            {/* Safari-like pull indicator: fades in with pull, spins when armed */}
            <Fade in={indicatorVisible} timeout={{ enter: 80, exit: 120 }}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 10,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: (t) => t.zIndex.snackbar + 2,
                        transform: `translateY(${indicatorOffset}px)`,
                        opacity: indicatorOpacity,
                        transition: 'opacity 80ms linear, transform 60ms linear',
                    }}
                >
                    <Box
                        sx={{
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 999,
                            bgcolor: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            boxShadow: 2,
                        }}
                    >
                        {/* Before threshold: determinate w/ tiny value so it’s a faint ring; after: indeterminate spin */}
                        {indicatorSpinning ? (
                            <CircularProgress size={18} thickness={6} color="inherit" />
                        ) : (
                            <CircularProgress
                                size={18}
                                thickness={6}
                                color="inherit"
                                variant="determinate"
                                value={Math.min((indicatorOpacity || 0) * 100, 95)} // visually “coming into focus”
                            />
                        )}
                    </Box>
                </Box>
            </Fade>

            {/* Updating Notification */}
            <Snackbar
                open={isUpdating}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ top: { xs: 70, sm: 80 } }}
            >
                <Alert severity="info" variant="filled" sx={{ width: '100%', maxWidth: 400 }}
                    icon={<CircularProgress size={20} color="inherit" />}>
                    <Typography variant="body2">Updating app...</Typography>
                </Alert>
            </Snackbar>

            {/* Update Success Notification */}
            <Snackbar
                open={showUpdateSuccess}
                autoHideDuration={3000}
                onClose={handleDismissSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ top: { xs: 70, sm: 80 } }}
            >
                <Alert severity="success" variant="filled" onClose={handleDismissSuccess}
                    sx={{ width: '100%', maxWidth: 400 }}>
                    <Typography variant="body2">App updated successfully!</Typography>
                </Alert>
            </Snackbar>
        </>
    );
};

export default PWAUpdateManager;
